import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getMarketingLocale } from '../lib/marketingLocale';
import fr from '../i18n/locales/fr';
import en from '../i18n/locales/en';
import { resolveLocale } from '../i18n/resolveLocale';
import { isRtlLocale } from '../i18n/localesConfig';
import { interpolateTranslation } from '../i18n/interpolate';
import { setActiveLocale } from '../i18n/activeLocale';

// Translation dictionaries
// MB-I18N-2A — dictionnaires externalisés (locales/fr, locales/en). AR legacy conservé hors runtime.
const translations = {
  fr,
  en,
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const [language, setLanguage] = useState(() => resolveLocale(localStorage.getItem('superkawa_lang')));
  // MB-I18N-3 — synchro immédiate du bridge locale (helpers module-level de formatage).
  setActiveLocale(language);

  useEffect(() => {
    localStorage.setItem('superkawa_lang', language);
    // MB-I18N-1 — direction pilotée par la locale (préparation RTL). fr/en → ltr.
    document.documentElement.dir = isRtlLocale(language) ? 'rtl' : 'ltr';
    // MB-SEO-EN-2 — <html lang> autoritaire : sur les routes marketing, la langue
    // suit la locale d'URL (/en/* → 'en', sinon 'fr') pour un signal linguistique
    // cohérent (prerender inclus). Sur /app/* (App Client / OS), on conserve la
    // langue applicative choisie par l'utilisateur.
    const isAppRoute = location.pathname.startsWith('/app');
    document.documentElement.lang = isAppRoute ? language : getMarketingLocale(location.pathname);
  }, [language, location.pathname]);

  const t = (key, params = {}) => {
    const text = translations[language]?.[key] || translations['fr']?.[key] || key;
    return interpolateTranslation(text, params);
  };

  const changeLanguage = (lang) => {
    setLanguage(resolveLocale(lang));
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export { translations };
